import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Users, ChevronRight, FileSpreadsheet } from "lucide-react";

export default function ContactsPage() {
    const [q, setQ] = useState("");
    const contacts = useQuery({
        queryKey: ["contacts", q],
        queryFn: () => api.get(`/contacts?q=${encodeURIComponent(q)}&limit=500`).then((r) => r.data),
    });
    const items = contacts.data?.items || [];
    const total = contacts.data?.total || 0;

    return (
        <div className="space-y-6 pv-enter">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="pv-eyebrow mb-2 flex items-center gap-2">
                        <FileSpreadsheet size={11} strokeWidth={1.5} /> Contatti importati
                    </div>
                    <h1 className="pv-serif text-4xl tracking-tight">{total} <span className="text-textmuted">{total === 1 ? "contatto reale" : "contatti reali"}</span></h1>
                    <p className="text-[13.5px] text-textmuted mt-1 max-w-2xl">
                        Contatti importati dal workbook Excel master. Ogni contatto conserva la provenance completa (file, foglio, riga).
                    </p>
                </div>
                <Link
                    to="/admin/import"
                    className="flex items-center gap-1.5 rounded-xl border border-border/[var(--border-opacity)] px-3 py-2 text-[12.5px] hover:bg-surface2"
                >
                    <FileSpreadsheet size={13} strokeWidth={1.5} /> Import Excel
                </Link>
            </div>

            <div className="flex items-center gap-2 rounded-xl border border-border/[var(--border-opacity)] bg-surface px-3 py-2 max-w-md">
                <Search size={14} strokeWidth={1.5} className="text-textmuted" />
                <input
                    data-testid="contacts-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Cerca nome, telefono, email, ID KER, città..."
                    className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-textsubtle"
                />
            </div>

            <div className="pv-card overflow-hidden">
                <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border/[var(--border-opacity)] text-[11px] uppercase tracking-[0.14em] text-textsubtle">
                    <div className="col-span-3">Nome</div>
                    <div className="col-span-2">Città · Regione</div>
                    <div className="col-span-2">Telefono</div>
                    <div className="col-span-2">Email</div>
                    <div className="col-span-1">Score</div>
                    <div className="col-span-1">Fonte</div>
                    <div className="col-span-1"></div>
                </div>
                <div className="divide-y divide-border/[var(--border-opacity)]">
                    {items.map((c) => (
                        <Link
                            key={c.id}
                            to={`/contacts/${c.id}`}
                            data-testid={`contact-row-${c.id}`}
                            className="grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-primary/[0.04] transition-colors"
                        >
                            <div className="col-span-3 min-w-0">
                                <div className="text-[13.5px] font-medium truncate">
                                    {[c.first_name, c.last_name].filter(Boolean).join(" ") || c.full_name || <span className="text-textsubtle">— nome mancante —</span>}
                                </div>
                                <div className="text-[11.5px] text-textsubtle truncate">
                                    {c.role || c.specialization || c.ker_id}
                                </div>
                            </div>
                            <div className="col-span-2 text-[12px] text-textmuted truncate">
                                {c.city || "—"}{c.regione && ` · ${c.regione}`}
                            </div>
                            <div className="col-span-2 text-[12px] pv-mono text-textmuted truncate">{c.phone_normalized || "—"}</div>
                            <div className="col-span-2 text-[12px] text-textmuted truncate">{c.email || "—"}</div>
                            <div className="col-span-1 pv-mono text-[12.5px]">{c.score ?? "—"}</div>
                            <div className="col-span-1 text-[11px] text-textmuted truncate">{c.original_source || c.crm_origin || "—"}</div>
                            <div className="col-span-1 flex justify-end">
                                <ChevronRight size={14} strokeWidth={1.5} className="text-textsubtle" />
                            </div>
                        </Link>
                    ))}
                    {items.length === 0 && (
                        <div className="px-5 py-16 text-center text-[13px] text-textsubtle">
                            {total === 0
                                ? <>Nessun contatto ancora importato. Vai a <Link to="/admin/import" className="text-primary hover:underline">Import Excel</Link> per caricare il workbook.</>
                                : "Nessun risultato per la ricerca."}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
